// Import axios config FIRST to set up interceptors before any other imports
import './axiosConfig';

import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { ThemeProvider } from "next-themes";
import LandingPage from "./pages/LandingPage";
import HomePage from "./pages/HomePage";
import CreatePage from "./pages/CreatePage";
import ExplorePage from "./pages/ExplorePage";
import SearchPage from "./pages/SearchPage";
import StoryViewer from "./pages/StoryViewer";
import LoginPage from "./pages/LoginPage";
import AuthCallback from "./pages/AuthCallback";
import LibraryPage from "./pages/LibraryPage";
import FavoritesPage from "./pages/FavoritesPage";
import ResetPasswordPage from "./pages/ResetPasswordPage";
import VerifyEmailPage from "./pages/VerifyEmailPage";
import EmailVerificationPendingPage from "./pages/EmailVerificationPendingPage";
import ProfilePage from "./pages/ProfilePage";
import ConnectionsPage from "./pages/ConnectionsPage";
import GeneratingPage from "./pages/GeneratingPage";
import AdminPanel from "./pages/AdminPanel";
import PricingPage from "./pages/PricingPage";
import PaymentSuccessPage from "./pages/PaymentSuccessPage";
import PaymentCancelPage from "./pages/PaymentCancelPage";
import ProtectedRoute from "./components/ProtectedRoute";
import { SeriesProvider } from "./contexts/SeriesContext";
import { TooltipProvider } from "@/components/ui/tooltip";
import SeriesListPage from "./pages/SeriesListPage";
import SeriesDetailPage from "./pages/SeriesDetailPage";
import PublicSeriesDetailPage from "./pages/PublicSeriesDetailPage";
import CreateSeriesPage from "./pages/CreateSeriesPage";
import CreateSeriesBookPage from "./pages/CreateSeriesBookPage";
import PublicUserProfilePage from "./pages/PublicUserProfilePage";
import HelpPage from "./pages/HelpPage";
import ContactPage from "./pages/ContactPage";
import TermsPage from "./pages/TermsPage";
import PrivacyPage from "./pages/PrivacyPage";
import ContentPolicyPage from "./pages/ContentPolicyPage";
import DmcaPolicyPage from "./pages/DmcaPolicyPage";
import TourPage from "./pages/TourPage";
import { usePageTracking } from "./hooks/useAnalytics";
import "@/App.css";

function AppRouter() {
  const location = useLocation();

  // Track page views for analytics
  usePageTracking();
  
  // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
  // Check URL fragment for session_id synchronously during render (prevents race conditions)
  if (location.hash?.includes('session_id=')) {
    return <AuthCallback />;
  }
  
  return (
    <Routes>
      {/* Public Routes */}
      <Route path="/" element={<LandingPage />} />
      <Route path="/tour" element={<TourPage />} />
      <Route path="/home" element={<HomePage />} />
      <Route path="/login" element={<LoginPage />} />
      <Route path="/auth/callback" element={<AuthCallback />} />
      <Route path="/reset-password" element={<ResetPasswordPage />} />
      <Route path="/verify-email" element={<VerifyEmailPage />} />
      <Route path="/verification-pending" element={<EmailVerificationPendingPage />} />
      <Route path="/explore" element={<ExplorePage />} />
      <Route path="/search" element={<SearchPage />} />
      <Route path="/create" element={<CreatePage />} />
      <Route path="/generating/:jobId" element={
        <ProtectedRoute>
          <GeneratingPage />
        </ProtectedRoute>
      } />
      <Route path="/story/:storyId" element={<StoryViewer />} />
      <Route path="/user/:userId" element={<PublicUserProfilePage />} />

      {/* Protected Routes */}
      <Route path="/library" element={
        <ProtectedRoute>
          <LibraryPage />
        </ProtectedRoute>
      } />
      <Route path="/favorites" element={
        <ProtectedRoute>
          <FavoritesPage />
        </ProtectedRoute>
      } />
      <Route path="/profile" element={
        <ProtectedRoute>
          <ProfilePage />
        </ProtectedRoute>
      } />
      <Route path="/profile/connections" element={
        <ProtectedRoute>
          <ConnectionsPage />
        </ProtectedRoute>
      } />
      <Route path="/admin" element={
        <ProtectedRoute>
          <AdminPanel />
        </ProtectedRoute>
      } />

      {/* Payment Routes */}
      <Route path="/pricing" element={<PricingPage />} />
      <Route path="/payment/success" element={<PaymentSuccessPage />} />
      <Route path="/payment/cancel" element={<PaymentCancelPage />} />

      {/* Help & Support Routes */}
      <Route path="/help" element={<HelpPage />} />
      <Route path="/contact" element={<ContactPage />} />
      <Route path="/terms" element={<TermsPage />} />
      <Route path="/privacy" element={<PrivacyPage />} />
      <Route path="/content-policy" element={<ContentPolicyPage />} />
      <Route path="/dmca" element={<DmcaPolicyPage />} />

      {/* Series Routes */}
      <Route path="/series" element={
        <ProtectedRoute>
          <SeriesListPage />
        </ProtectedRoute>
      } />
      <Route path="/series/create" element={
        <ProtectedRoute>
          <CreateSeriesPage />
        </ProtectedRoute>
      } />
      <Route path="/series/public/:seriesId" element={<PublicSeriesDetailPage />} />
      <Route path="/series/:seriesId" element={
        <ProtectedRoute>
          <SeriesDetailPage />
        </ProtectedRoute>
      } />
      <Route path="/series/:seriesId/create-book" element={
        <ProtectedRoute>
          <CreateSeriesBookPage />
        </ProtectedRoute>
      } />
    </Routes>
  );
}

function App() {
  return (
    <ThemeProvider
      attribute="class"
      defaultTheme="system"
      enableSystem={true}
      disableTransitionOnChange
    >
      <div className="App">
        <TooltipProvider>
          <BrowserRouter>
            <SeriesProvider>
              <AppRouter />
            </SeriesProvider>
          </BrowserRouter>
        </TooltipProvider>
      </div>
    </ThemeProvider>
  );
}

export default App;
